/**
 * Generic Reusable Carousel Component
 * Built on top of Shadcn Carousel (powered by Embla)
 * Can be used anywhere with any data type and custom render function
 */

import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures"
import {
  CarouselContent,
  CarouselItem,
  CarouselNext,
  type CarouselOptions,
  CarouselPrevious,
  Carousel as ShadcnCarousel,
} from "@/components/ui/carousel"
import { useBreakpointItems } from "@/hooks/useBreakpoints"

/**
 * Configuration for number of items to display per screen size
 * All properties are optional with sensible defaults
 */
export interface ResponsiveItemsConfig {
  sm?: number // Items on small screens (default: 1)
  md?: number // Items on medium screens (default: 2)
  lg?: number // Items on large screens (default: 3)
  xl?: number // Items on extra large screens (default: 3)
}

export interface CarouselProps<T> {
  data: T[]
  renderItem: (item: T, index: number) => React.ReactNode
  className?: string
  itemsPerScreen?: ResponsiveItemsConfig // Optional responsive configuration
  itemWidth?: string // Optional fixed width for items (e.g., "520px")
  loop?: boolean // Enable circular scrolling (default: true)
  endPadding?: string // Padding at the end (e.g., "6px")
  showArrows?: boolean // Show prev/next arrow buttons (default: false)
  arrowClassName?: string // Custom className for arrow buttons
  opts?: Partial<CarouselOptions> // Override default embla options
  contentClassName?: string // Custom className for CarouselContent (e.g., gap)
}

const defaultOpts: CarouselOptions = {
  align: "center",
  dragFree: true,
  containScroll: "trimSnaps",
  loop: true,
  duration: 25,
  startIndex: 0,
}

export function Carousel<T>({
  data,
  renderItem,
  className = "",
  itemsPerScreen,
  itemWidth,
  loop = true,
  endPadding,
  showArrows = false,
  arrowClassName,
  opts,
  contentClassName,
}: CarouselProps<T>) {
  const config = {
    sm: itemsPerScreen?.sm ?? 1,
    md: itemsPerScreen?.md ?? 2,
    lg: itemsPerScreen?.lg ?? 3,
    xl: itemsPerScreen?.xl ?? 3,
  }

  const items = useBreakpointItems(config)
  const percentWidth = `${100 / items}%`

  // Use fixed width if provided, otherwise use percentage-based width
  const itemStyle = itemWidth
    ? { flex: `0 0 ${itemWidth}`, maxWidth: itemWidth }
    : { flex: `0 0 ${percentWidth}`, maxWidth: percentWidth }

  const mergedOpts: CarouselOptions = { ...defaultOpts, loop, ...opts }

  return (
    <ShadcnCarousel
      opts={mergedOpts}
      plugins={[WheelGesturesPlugin()]}
      className={`w-full ${className}`}
    >
      <CarouselContent
        className={contentClassName ? `ml-0 ${contentClassName}` : "gap-[5px] touch-pan-x"}
      >
        {data.map((item, index) => (
          <CarouselItem
            key={`carousel-${index}`}
            style={itemStyle}
            className={contentClassName ? "pl-5" : ""}
          >
            {renderItem(item, index)}
          </CarouselItem>
        ))}
        {endPadding && <div className={endPadding} style={{ flex: "0 0 auto" }} />}
      </CarouselContent>
      {showArrows && data.length > 1 && (
        <>
          <CarouselPrevious className={arrowClassName} />
          <CarouselNext className={arrowClassName} />
        </>
      )}
    </ShadcnCarousel>
  )
}
